/**
 * API Client Utility
 * 
 * Centralized HTTP client for making API requests to the backend
 * Handles authentication, error handling, and response parsing
 */

import { env } from '@/lib/config/env';

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class ApiClient {
  private baseURL: string;

  constructor() {
    // Use relative URL for same-origin requests, or absolute URL if different origin
    this.baseURL = typeof window !== 'undefined' 
      ? '/api/v1' 
      : `${env.API_BASE_URL}/api/v1`;
  }

  /**
   * Get authentication token from localStorage
   */
  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('accessToken');
  }

  /**
   * Set authentication token in localStorage and cookie
   */
  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('accessToken', token);
    // Also set cookie for middleware access
    document.cookie = `accessToken=${token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
  }

  /**
   * Remove authentication token from localStorage and cookies
   */
  removeToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    // Remove cookies
    document.cookie = 'accessToken=; path=/; max-age=0';
    document.cookie = 'refreshToken=; path=/; max-age=0';
  }

  /**
   * Set refresh token in localStorage and cookie
   */
  setRefreshToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('refreshToken', token);
    // Also set cookie for middleware access
    document.cookie = `refreshToken=${token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
  }

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('refreshToken');
  }

  /**
   * Build request headers
   */
  private getHeaders(customHeaders?: HeadersInit): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   * On 401 for /operator/me, clears tokens and redirects to operator login
   */
  private async handleResponse<T>(response: Response, endpoint?: string): Promise<ApiResponse<T>> {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401 && endpoint === '/operator/me') {
        this.removeToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/operator-login';
        }
      }
      return {
        success: false,
        error: {
          code: data.error?.code || 'UNKNOWN_ERROR',
          message: data.error?.message || 'An error occurred',
          details: data.error?.details,
        },
      };
    }

    return {
      success: true,
      data: data.data || data,
      pagination: data.pagination,
    };
  }

  /**
   * Make GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      // Build URL - handle both relative and absolute baseURL
      let urlString: string;
      if (this.baseURL.startsWith('http://') || this.baseURL.startsWith('https://')) {
        // Absolute URL - use URL constructor
        const url = new URL(endpoint, this.baseURL);
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              if (Array.isArray(value)) {
                value.forEach((v) => url.searchParams.append(key, String(v)));
              } else {
                url.searchParams.set(key, String(value));
              }
            }
          });
        }
        urlString = url.toString();
      } else {
        // Relative URL - use string concatenation
        urlString = `${this.baseURL}${endpoint}`;
        if (params) {
          const searchParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(key, String(v)));
              } else {
                searchParams.set(key, String(value));
              }
            }
          });
          const queryString = searchParams.toString();
          if (queryString) {
            urlString += `?${queryString}`;
          }
        }
      }

      const response = await fetch(urlString, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      return this.handleResponse<T>(response, endpoint);
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }

  /**
   * Make POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      // Handle both relative URLs (with query params) and absolute URLs
      let urlString: string;
      if (this.baseURL.startsWith('http://') || this.baseURL.startsWith('https://')) {
        // Absolute URL - use URL constructor
        urlString = new URL(endpoint, this.baseURL).toString();
      } else {
        // Relative URL - use string concatenation
        urlString = `${this.baseURL}${endpoint}`;
      }

      const response = await fetch(urlString, {
        method: 'POST',
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response, endpoint);
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }

  /**
   * Make PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response, endpoint);
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }

  /**
   * Make PATCH request
   */
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response, endpoint);
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }

  /**
   * Make DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return this.handleResponse<T>(response, endpoint);
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
